/*
 * KNS Hub Filters — unified engine for metaobject hub pages (surface_finish,
 * profile, line, steel, maker, and future hub types).
 *
 * Replaces 4 near-duplicate per-hub filter snippets (~1700 LoC) with one
 * configurable engine + a thin Liquid bootstrap. Operator-architected
 * "no click-and-wait" pattern: fetch all pages upfront, then pure client-side
 * filtering via classList toggles. Filter clicks are visually instant.
 *
 * Boot contract: a hub page emits one
 *   <script type="application/json" data-kns-hub-filters-config>{...}</script>
 * per filter root, plus matching markup: a product grid <div id="{prefix}-prod-grid">
 * with .{prefix}-prod-card children carrying data-* filter attributes, a filter
 * container <div id="{prefix}-filters">, and an optional counter
 * <span id="{prefix}-filter-count">.
 *
 * The engine never assumes specific data-* attribute names beyond what
 * config.filterTypes declares. Card markup is the hub's responsibility; the
 * engine only reads attrs and toggles classes.
 *
 * Improvements vs the legacy per-hub snippets:
 *   - DOM-API pill construction (no innerHTML string concat; auto-escaped labels)
 *   - aria-pressed on active pills + aria-live counter (screen-reader friendly)
 *   - Cross-filter-aware pill counts (configurable; default ON)
 *   - URL-state hydration + replaceState on filter changes (configurable; default OFF)
 *   - CSS-driven FOUC suppression via .kns-hub-grid--ready toggle
 *   - Idempotent re-init for async-loaded card batches (replaces maker's
 *     150-line internal copy-paste)
 *   - Optional console.warn when filterTypes references missing data-attrs
 *     (debug mode; silent in production)
 */
(function () {
  'use strict';

  // Per-root init guard. We mark each root with a data flag after initRoot
  // succeeds, so re-running initAll on dynamically-injected sections will
  // pick up new roots without re-initializing existing ones. This replaces
  // a global window flag, which would have blocked all subsequent script
  // tags (Shopify sometimes injects sections lazily).
  var INIT_ATTR = 'data-kns-hub-filters-initialized';

  function initAll() {
    document.querySelectorAll('[data-kns-hub-filters-root]:not([' + INIT_ATTR + '])').forEach(function (root) {
      try {
        initRoot(root);
        root.setAttribute(INIT_ATTR, '1');
      } catch (e) {
        console.error('[kns-hub-filters] initRoot failed', e);
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Expose so a host page can re-trigger after lazy-injecting sections.
  window.knsHubFiltersInit = initAll;

  function initRoot(root) {
    var configEl = root.querySelector('script[data-kns-hub-filters-config]');
    if (!configEl) return;
    var cfg;
    try { cfg = JSON.parse(configEl.textContent || '{}'); }
    catch (e) { console.error('[kns-hub-filters] invalid config JSON', e); return; }

    var prefix = cfg.prefix;
    if (!prefix) { console.error('[kns-hub-filters] config.prefix required'); return; }

    var grid      = document.getElementById(prefix + '-prod-grid');
    var filterBox = document.getElementById(prefix + '-filters');
    var counter   = document.getElementById(prefix + '-filter-count');
    if (!grid || !filterBox) {
      console.warn('[kns-hub-filters] grid or filter container missing for prefix=' + prefix);
      return;
    }

    var ctx = buildContext(cfg, grid, filterBox, counter);
    var total = parseInt(grid.dataset.total, 10) || 0;
    var pageSize = parseInt(grid.dataset.pageSize, 10) || 250;
    var pages = Math.ceil(total / pageSize);

    if (cfg.fetchMode === 'page' && pages > 1) {
      loadAdditionalPages(ctx, pages, total).then(function () { bootFilters(ctx); });
    } else {
      bootFilters(ctx);
    }

    // Expose a refresh hook for hub pages that load more cards async (e.g.
    // maker page's deferred section loading). Call this AFTER appending new
    // cards into grid; it rebuilds the filter UI preserving active state.
    root.knsHubFiltersRefresh = function () { rebuildFilters(ctx); };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Context: shared state object passed through helpers
  // ─────────────────────────────────────────────────────────────────────────
  function buildContext(cfg, grid, filterBox, counter) {
    var labels = cfg.labels || {};
    var ctx = {
      cfg: cfg,
      prefix: cfg.prefix,
      grid: grid,
      filterBox: filterBox,
      counter: counter,
      filterTypes: cfg.filterTypes || [],
      collapsible: toSet(cfg.collapsible),
      popular: cfg.popular || {},
      mode: cfg.mode === 'multi' ? 'multi' : 'single',
      crossFilterCounts: cfg.crossFilterCounts !== false, // default ON
      urlState: !!cfg.urlState,
      labels: labels,
      collapseLimit: cfg.collapseLimit || 6,
      batchDesktop: cfg.batchDesktop || 48,
      batchMobile: cfg.batchMobile || 24,
      orderedBuckets: {
        price: cfg.priceOrder || ['Under $150', '$150 - $300', '$300 - $500', '$500+'],
        length: cfg.lengthOrder || ['Under 165mm', '165-185mm', '185-220mm', '220-255mm', '255-270mm', '270mm+']
      },
      activeFilters: emptyActive(cfg),
      onlyInStock: false,
      onlyOnSale: false,
      visibleLimit: 0,
      cards: [],
      cardData: [],
      pills: [],
      resetBtn: null,
      stockBtn: null,
      saleBtn: null,
      showMoreBtn: null
    };
    return ctx;
  }

  function emptyActive(cfg) {
    var out = {};
    (cfg.filterTypes || []).forEach(function (t) {
      out[t] = cfg.mode === 'multi' ? new Set() : null;
    });
    return out;
  }

  function toSet(maybeArr) {
    var s = new Set();
    (maybeArr || []).forEach(function (k) { s.add(k); });
    return s;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Page fetching (for hubs whose collection exceeds Shopify's paginate-by)
  // ─────────────────────────────────────────────────────────────────────────
  function loadAdditionalPages(ctx, pages, total) {
    var loader = document.createElement('div');
    loader.className = ctx.prefix + '-page-loader kns-hub-page-loader';
    loader.setAttribute('aria-live', 'polite');
    loader.textContent = (ctx.labels.loadingAll || 'Loading all ') + total + (ctx.labels.knivesSuffix || ' knives…');
    ctx.grid.parentNode.insertBefore(loader, ctx.grid.nextSibling);

    var base = window.location.href.split('?')[0].split('#')[0];
    var promises = [];
    for (var p = 2; p <= pages; p++) {
      promises.push(fetchPageInto(ctx, base, p));
    }
    return Promise.all(promises).then(function () {
      loader.remove();
    }).catch(function () {
      loader.textContent = (ctx.labels.loadError || 'Some products could not be loaded.');
      loader.style.opacity = '.5';
    });
  }

  function fetchPageInto(ctx, base, pageNum) {
    return fetch(base + '?page=' + pageNum, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('#' + ctx.prefix + '-prod-grid .' + ctx.prefix + '-prod-card').forEach(function (card) {
          ctx.grid.appendChild(document.importNode(card, true));
        });
      });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Filter initialization
  // ─────────────────────────────────────────────────────────────────────────
  function bootFilters(ctx) {
    sortSoldOutToBottom(ctx);
    readCards(ctx);
    if (ctx.cfg.warnMissingAttrs) warnMissingAttrs(ctx);
    buildPills(ctx);
    if (ctx.urlState) hydrateFromUrl(ctx);
    ctx.visibleLimit = getBatch(ctx);
    applyFilters(ctx);
    // FOUC release: now that DOM is correctly hidden/shown, mark the grid
    // ready so CSS un-hides the visible cards.
    ctx.grid.classList.add('kns-hub-grid--ready');
  }

  // Rebuild filters after new cards were appended (deferred maker case).
  // Preserves activeFilters / onlyInStock / onlyOnSale state.
  function rebuildFilters(ctx) {
    sortSoldOutToBottom(ctx);
    readCards(ctx);
    // Drop active filter values that no longer exist in any card (in case
    // the new cards changed the available option set).
    pruneActiveFilters(ctx);
    buildPills(ctx);
    applyFilters(ctx);
  }

  function readCards(ctx) {
    ctx.cards = Array.prototype.slice.call(ctx.grid.querySelectorAll('.' + ctx.prefix + '-prod-card'));
    ctx.cardData = ctx.cards.map(function (card) {
      var d = {};
      ctx.filterTypes.forEach(function (t) { d[t] = card.dataset[t] || ''; });
      return d;
    });
  }

  function warnMissingAttrs(ctx) {
    ctx.filterTypes.forEach(function (t) {
      var anyHasIt = ctx.cardData.some(function (d) { return d[t]; });
      if (!anyHasIt) console.warn('[kns-hub-filters] filter "' + t + '" declared but no card has data-' + t);
    });
  }

  function pruneActiveFilters(ctx) {
    var allValues = {};
    ctx.filterTypes.forEach(function (t) { allValues[t] = new Set(); });
    ctx.cardData.forEach(function (d) {
      ctx.filterTypes.forEach(function (t) { if (d[t]) allValues[t].add(d[t]); });
    });
    ctx.filterTypes.forEach(function (t) {
      if (ctx.mode === 'multi') {
        var keep = new Set();
        ctx.activeFilters[t].forEach(function (v) { if (allValues[t].has(v)) keep.add(v); });
        ctx.activeFilters[t] = keep;
      } else {
        if (ctx.activeFilters[t] && !allValues[t].has(ctx.activeFilters[t])) ctx.activeFilters[t] = null;
      }
    });
  }

  function sortSoldOutToBottom(ctx) {
    var cards = Array.prototype.slice.call(ctx.grid.querySelectorAll('.' + ctx.prefix + '-prod-card'));
    var inStock = [];
    var soldOut = [];
    cards.forEach(function (card) {
      if (card.classList.contains(ctx.prefix + '-prod-card--soldout')) soldOut.push(card);
      else inStock.push(card);
    });
    inStock.concat(soldOut).forEach(function (card) { ctx.grid.appendChild(card); });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Pill construction (DOM API — auto-escaped, no innerHTML concat)
  // ─────────────────────────────────────────────────────────────────────────
  function buildPills(ctx) {
    // Compute available options + counts per dimension
    var options = {};
    var counts = {};
    ctx.filterTypes.forEach(function (t) { options[t] = new Set(); counts[t] = {}; });
    ctx.cardData.forEach(function (d) {
      ctx.filterTypes.forEach(function (t) {
        if (d[t]) {
          options[t].add(d[t]);
          counts[t][d[t]] = (counts[t][d[t]] || 0) + 1;
        }
      });
    });

    var sorted = {};
    ctx.filterTypes.forEach(function (t) {
      if (ctx.orderedBuckets[t]) {
        sorted[t] = ctx.orderedBuckets[t].filter(function (v) { return options[t].has(v); });
      } else {
        sorted[t] = Array.from(options[t]).sort(function (a, b) {
          var diff = (counts[t][b] || 0) - (counts[t][a] || 0);
          return diff !== 0 ? diff : a.localeCompare(b);
        });
      }
    });

    // Clear filterBox + render fresh
    while (ctx.filterBox.firstChild) ctx.filterBox.removeChild(ctx.filterBox.firstChild);

    ctx.filterTypes.forEach(function (t) {
      // Render any non-empty dimension. Single-option groups remain useful:
      // they communicate "all current items share this value" (e.g. all SKD
      // steel knives are from Hatsukokoro) and the pill's count still
      // updates dynamically as other filters narrow the set.
      if (sorted[t].length < 1) return;
      var group = el('div', { class: ctx.prefix + '-filters__group kns-hub-filters__group', 'data-group': t });
      group.appendChild(el('span', { class: ctx.prefix + '-filters__label kns-hub-filters__label' }, [text(ctx.labels[t] || titleCase(t))]));

      var canCollapse = ctx.collapsible.has(t) && sorted[t].length > ctx.collapseLimit;
      sorted[t].forEach(function (val, idx) {
        var pill = el('button', {
          type: 'button',
          class: ctx.prefix + '-filter-pill kns-hub-pill' +
            (canCollapse && idx >= ctx.collapseLimit ? ' ' + ctx.prefix + '-filter-pill--collapsed kns-hub-pill--collapsed' : '') +
            (ctx.popular[t] && ctx.popular[t].indexOf(val) !== -1 ? ' ' + ctx.prefix + '-filter-pill--popular kns-hub-pill--popular' : ''),
          'data-filter-type': t,
          'data-filter-value': val,
          'aria-pressed': 'false'
        }, [
          text(val + ' '),
          el('span', { class: ctx.prefix + '-pill-count kns-hub-pill__count' }, [text('(' + (counts[t][val] || 0) + ')')])
        ]);
        pill.addEventListener('click', function () { togglePill(ctx, pill); });
        group.appendChild(pill);
      });

      if (canCollapse) {
        var extra = sorted[t].length - ctx.collapseLimit;
        var expandBtn = el('button', {
          type: 'button',
          class: ctx.prefix + '-filter-pill ' + ctx.prefix + '-filter-expand kns-hub-pill kns-hub-pill--expand',
          'data-expand-group': t,
          'data-extra-count': extra
        }, [text('+ ' + extra + ' ' + (ctx.labels.moreSuffix || 'more'))]);
        expandBtn.addEventListener('click', function () { toggleExpand(ctx, expandBtn, t, extra); });
        group.appendChild(expandBtn);
      }

      ctx.filterBox.appendChild(group);
    });

    // Stock / Sale / Reset buttons
    var stockCount = 0, saleCount = 0;
    ctx.cards.forEach(function (c) {
      if (!c.classList.contains(ctx.prefix + '-prod-card--soldout')) stockCount++;
      if (c.classList.contains(ctx.prefix + '-prod-card--onsale')) saleCount++;
    });

    var resetGroup = el('div', { class: ctx.prefix + '-filters__group ' + ctx.prefix + '-filters__group--reset kns-hub-filters__group--reset' });

    if (saleCount > 0) {
      ctx.saleBtn = el('button', {
        type: 'button',
        class: ctx.prefix + '-filter-pill ' + ctx.prefix + '-filter-sale kns-hub-pill kns-hub-pill--sale' + (ctx.onlyOnSale ? ' is-active' : ''),
        'aria-pressed': ctx.onlyOnSale ? 'true' : 'false'
      }, [
        el('span', { class: ctx.prefix + '-sale-icon kns-hub-pill__icon-sale' }, [text('%')]),
        text(' ' + (ctx.labels.onSale || 'On Sale') + ' '),
        el('span', { class: ctx.prefix + '-pill-count kns-hub-pill__count' }, [text('(' + saleCount + ')')])
      ]);
      ctx.saleBtn.addEventListener('click', function () {
        ctx.onlyOnSale = !ctx.onlyOnSale;
        ctx.visibleLimit = getBatch(ctx);
        applyFilters(ctx);
      });
      resetGroup.appendChild(ctx.saleBtn);
    }

    if (stockCount < ctx.cards.length) {
      ctx.stockBtn = el('button', {
        type: 'button',
        class: ctx.prefix + '-filter-pill ' + ctx.prefix + '-filter-oos kns-hub-pill kns-hub-pill--stock' + (ctx.onlyInStock ? ' is-active' : ''),
        'aria-pressed': ctx.onlyInStock ? 'true' : 'false'
      }, [
        el('span', { class: ctx.prefix + '-oos-icon kns-hub-pill__icon-stock' }),
        text(' ' + (ctx.labels.inStock || 'In Stock') + ' '),
        el('span', { class: ctx.prefix + '-pill-count kns-hub-pill__count' }, [text('(' + stockCount + ')')])
      ]);
      ctx.stockBtn.addEventListener('click', function () {
        ctx.onlyInStock = !ctx.onlyInStock;
        ctx.visibleLimit = getBatch(ctx);
        applyFilters(ctx);
      });
      resetGroup.appendChild(ctx.stockBtn);
    }

    ctx.resetBtn = el('button', {
      type: 'button',
      class: ctx.prefix + '-filter-pill ' + ctx.prefix + '-filter-reset kns-hub-pill kns-hub-pill--reset',
      style: 'display:none'
    }, [
      crossSvg(),
      text(' ' + (ctx.labels.reset || 'Reset Filters'))
    ]);
    ctx.resetBtn.addEventListener('click', function () { resetAll(ctx); });
    resetGroup.appendChild(ctx.resetBtn);

    ctx.filterBox.appendChild(resetGroup);

    // Cache pill list (dimension pills only — exclude reset/sale/stock/expand)
    ctx.pills = Array.prototype.slice.call(ctx.filterBox.querySelectorAll('.' + ctx.prefix + '-filter-pill[data-filter-type]'));

    // Restore active-state classes (for the rebuild case)
    ctx.pills.forEach(function (pill) {
      if (isPillActive(ctx, pill)) {
        pill.classList.add('is-active');
        pill.setAttribute('aria-pressed', 'true');
      }
    });
  }

  function isPillActive(ctx, pill) {
    var t = pill.dataset.filterType;
    var v = pill.dataset.filterValue;
    return ctx.mode === 'multi' ? ctx.activeFilters[t].has(v) : ctx.activeFilters[t] === v;
  }

  function togglePill(ctx, pill) {
    var t = pill.dataset.filterType;
    var v = pill.dataset.filterValue;
    if (ctx.mode === 'multi') {
      if (ctx.activeFilters[t].has(v)) {
        ctx.activeFilters[t].delete(v);
        pill.classList.remove('is-active');
        pill.setAttribute('aria-pressed', 'false');
      } else {
        ctx.activeFilters[t].add(v);
        pill.classList.add('is-active');
        pill.setAttribute('aria-pressed', 'true');
      }
    } else {
      if (ctx.activeFilters[t] === v) {
        ctx.activeFilters[t] = null;
        pill.classList.remove('is-active');
        pill.setAttribute('aria-pressed', 'false');
      } else {
        ctx.pills.forEach(function (p) {
          if (p.dataset.filterType === t) {
            p.classList.remove('is-active');
            p.setAttribute('aria-pressed', 'false');
          }
        });
        ctx.activeFilters[t] = v;
        pill.classList.add('is-active');
        pill.setAttribute('aria-pressed', 'true');
      }
    }
    ctx.visibleLimit = getBatch(ctx);
    applyFilters(ctx);
    if (ctx.urlState) writeToUrl(ctx);
  }

  function toggleExpand(ctx, btn, group, extra) {
    var collapsed = ctx.filterBox.querySelectorAll('.' + ctx.prefix + '-filter-pill--collapsed[data-filter-type="' + cssEscape(group) + '"]');
    if (collapsed.length > 0) {
      collapsed.forEach(function (p) { p.classList.remove(ctx.prefix + '-filter-pill--collapsed'); p.classList.remove('kns-hub-pill--collapsed'); });
      while (btn.firstChild) btn.removeChild(btn.firstChild);
      btn.appendChild(text('− ' + (ctx.labels.viewLess || 'View less')));
    } else {
      var pillsInGroup = ctx.filterBox.querySelectorAll('.' + ctx.prefix + '-filter-pill[data-filter-type="' + cssEscape(group) + '"]:not(.' + ctx.prefix + '-filter-expand)');
      for (var i = ctx.collapseLimit; i < pillsInGroup.length; i++) {
        pillsInGroup[i].classList.add(ctx.prefix + '-filter-pill--collapsed');
        pillsInGroup[i].classList.add('kns-hub-pill--collapsed');
      }
      while (btn.firstChild) btn.removeChild(btn.firstChild);
      btn.appendChild(text('+ ' + extra + ' ' + (ctx.labels.moreSuffix || 'more')));
    }
  }

  function resetAll(ctx) {
    ctx.filterTypes.forEach(function (t) {
      ctx.activeFilters[t] = ctx.mode === 'multi' ? new Set() : null;
    });
    ctx.onlyInStock = false;
    ctx.onlyOnSale = false;
    ctx.pills.forEach(function (p) {
      p.classList.remove('is-active');
      p.setAttribute('aria-pressed', 'false');
    });
    if (ctx.stockBtn) { ctx.stockBtn.classList.remove('is-active'); ctx.stockBtn.setAttribute('aria-pressed', 'false'); }
    if (ctx.saleBtn) { ctx.saleBtn.classList.remove('is-active'); ctx.saleBtn.setAttribute('aria-pressed', 'false'); }
    ctx.visibleLimit = getBatch(ctx);
    applyFilters(ctx);
    if (ctx.urlState) writeToUrl(ctx);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Filter application + cross-filter-aware pill counts
  // ─────────────────────────────────────────────────────────────────────────
  function applyFilters(ctx) {
    var hasActive = anyFilterActive(ctx);
    var matchCount = 0;
    var shownCount = 0;

    ctx.cards.forEach(function (card, i) {
      var matches = cardMatches(ctx, ctx.cardData[i], i);
      if (matches) {
        matchCount++;
        if (matchCount <= ctx.visibleLimit) {
          card.classList.remove('is-hidden');
          shownCount++;
        } else {
          card.classList.add('is-hidden');
        }
      } else {
        card.classList.add('is-hidden');
      }
    });

    // Cross-filter pill hiding + count update
    ctx.pills.forEach(function (pill) {
      var t = pill.dataset.filterType;
      var v = pill.dataset.filterValue;

      // Pill visibility: is this pill already active? Always show.
      var alreadyActive = ctx.mode === 'multi' ? ctx.activeFilters[t].has(v) : ctx.activeFilters[t] === v;
      if (alreadyActive) {
        pill.classList.remove('is-hidden');
      } else {
        // Would adding this filter still match at least one card?
        // try/finally ensures activeFilters state is restored even if
        // cardMatches throws (codex review 2026-05-23 — defensive guard).
        var c = 0;
        var savedSingle;
        if (ctx.mode === 'multi') { ctx.activeFilters[t].add(v); }
        else { savedSingle = ctx.activeFilters[t]; ctx.activeFilters[t] = v; }
        try {
          for (var i = 0; i < ctx.cardData.length; i++) {
            if (cardMatches(ctx, ctx.cardData[i], i)) c++;
          }
        } finally {
          if (ctx.mode === 'multi') { ctx.activeFilters[t].delete(v); }
          else { ctx.activeFilters[t] = savedSingle; }
        }
        pill.classList.toggle('is-hidden', c === 0);

        // Update pill count (cross-filter-aware if config flag on)
        var countEl = pill.querySelector('.' + ctx.prefix + '-pill-count');
        if (countEl) {
          if (ctx.crossFilterCounts) {
            countEl.textContent = '(' + c + ')';
          } else {
            // Legacy behavior: count respects stock/sale only, not other filters
            var legacyC = 0;
            for (var j = 0; j < ctx.cardData.length; j++) {
              var card = ctx.cards[j];
              if (ctx.onlyInStock && card.classList.contains(ctx.prefix + '-prod-card--soldout')) continue;
              if (ctx.onlyOnSale && !card.classList.contains(ctx.prefix + '-prod-card--onsale')) continue;
              if (ctx.cardData[j][t] === v) legacyC++;
            }
            countEl.textContent = '(' + legacyC + ')';
          }
        }
      }
    });

    // Stock + sale button counts (always reflect "this toggle alone would yield N")
    if (ctx.stockBtn) {
      var stockC = 0;
      ctx.cards.forEach(function (c) { if (!c.classList.contains(ctx.prefix + '-prod-card--soldout')) stockC++; });
      var stockCountEl = ctx.stockBtn.querySelector('.' + ctx.prefix + '-pill-count');
      if (stockCountEl) stockCountEl.textContent = '(' + stockC + ')';
      ctx.stockBtn.classList.toggle('is-active', ctx.onlyInStock);
      ctx.stockBtn.setAttribute('aria-pressed', ctx.onlyInStock ? 'true' : 'false');
    }
    if (ctx.saleBtn) {
      var saleC = 0;
      ctx.cards.forEach(function (c) { if (c.classList.contains(ctx.prefix + '-prod-card--onsale')) saleC++; });
      var saleCountEl = ctx.saleBtn.querySelector('.' + ctx.prefix + '-pill-count');
      if (saleCountEl) saleCountEl.textContent = '(' + saleC + ')';
      ctx.saleBtn.classList.toggle('is-active', ctx.onlyOnSale);
      ctx.saleBtn.setAttribute('aria-pressed', ctx.onlyOnSale ? 'true' : 'false');
    }

    if (ctx.counter) {
      ctx.counter.setAttribute('aria-live', 'polite');
      ctx.counter.textContent = (ctx.labels.counter
        ? interp(ctx.labels.counter, { shown: shownCount, total: matchCount, knives: matchCount === 1 ? 'knife' : 'knives' })
        : shownCount + ' of ' + matchCount + ' ' + (matchCount === 1 ? 'knife' : 'knives'));
    }

    if (ctx.resetBtn) ctx.resetBtn.style.display = (hasActive || ctx.onlyInStock || ctx.onlyOnSale) ? '' : 'none';

    ensureShowMoreBtn(ctx);
    updateShowMore(ctx, shownCount, matchCount);
  }

  function anyFilterActive(ctx) {
    for (var i = 0; i < ctx.filterTypes.length; i++) {
      var t = ctx.filterTypes[i];
      if (ctx.mode === 'multi' ? ctx.activeFilters[t].size > 0 : ctx.activeFilters[t] !== null) return true;
    }
    return false;
  }

  function cardMatches(ctx, d, idx) {
    if (ctx.onlyInStock && ctx.cards[idx] && ctx.cards[idx].classList.contains(ctx.prefix + '-prod-card--soldout')) return false;
    if (ctx.onlyOnSale && ctx.cards[idx] && !ctx.cards[idx].classList.contains(ctx.prefix + '-prod-card--onsale')) return false;
    for (var i = 0; i < ctx.filterTypes.length; i++) {
      var t = ctx.filterTypes[i];
      var f = ctx.activeFilters[t];
      if (ctx.mode === 'multi') {
        if (f.size === 0) continue;
        if (!f.has(d[t])) return false;
      } else {
        if (f === null) continue;
        if (d[t] !== f) return false;
      }
    }
    return true;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Show More
  // ─────────────────────────────────────────────────────────────────────────
  function ensureShowMoreBtn(ctx) {
    if (ctx.showMoreBtn && document.body.contains(ctx.showMoreBtn)) return;
    var wrap = el('div', { class: ctx.prefix + '-show-more-wrap kns-hub-show-more-wrap', id: ctx.prefix + '-show-more-wrap' });
    var btn = el('button', { type: 'button', class: ctx.prefix + '-show-more-btn kns-hub-show-more-btn' });
    wrap.appendChild(btn);
    ctx.grid.parentNode.insertBefore(wrap, ctx.grid.nextSibling);
    btn.addEventListener('click', function () {
      ctx.visibleLimit += getBatch(ctx);
      applyFilters(ctx);
    });
    ctx.showMoreBtn = btn;
  }

  function updateShowMore(ctx, shownCount, matchCount) {
    var wrap = ctx.showMoreBtn && ctx.showMoreBtn.parentElement;
    if (!wrap) return;
    if (shownCount < matchCount) {
      wrap.style.display = '';
      var remaining = matchCount - shownCount;
      while (ctx.showMoreBtn.firstChild) ctx.showMoreBtn.removeChild(ctx.showMoreBtn.firstChild);
      ctx.showMoreBtn.appendChild(text(
        ctx.labels.showMoreFmt
          ? interp(ctx.labels.showMoreFmt, { remaining: remaining })
          : 'Show More (' + remaining + ' remaining)'
      ));
      ctx.showMoreBtn.appendChild(text(' '));
      ctx.showMoreBtn.appendChild(chevronSvg());
    } else {
      wrap.style.display = 'none';
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // URL state
  // ─────────────────────────────────────────────────────────────────────────
  function writeToUrl(ctx) {
    var url = new URL(window.location.href);
    // Clear ALL existing values for managed keys (handles repeated keys too).
    ctx.filterTypes.concat(['oos', 'sale']).forEach(function (k) { url.searchParams.delete(k); });

    // Multi-mode emits repeated keys (?maker=A&maker=B), not comma-joined,
    // so values containing commas round-trip cleanly (codex finding 2026-05-23).
    ctx.filterTypes.forEach(function (t) {
      if (ctx.mode === 'multi') {
        Array.from(ctx.activeFilters[t]).forEach(function (v) { url.searchParams.append(t, v); });
      } else {
        if (ctx.activeFilters[t] !== null) url.searchParams.set(t, ctx.activeFilters[t]);
      }
    });
    if (ctx.onlyInStock) url.searchParams.set('oos', '1');
    if (ctx.onlyOnSale) url.searchParams.set('sale', '1');

    // Replace, don't push, to avoid bloating history.
    window.history.replaceState({}, '', url.toString());
  }

  function hydrateFromUrl(ctx) {
    var params = new URLSearchParams(window.location.search);
    ctx.filterTypes.forEach(function (t) {
      if (ctx.mode === 'multi') {
        // getAll returns array of all repeated keys.
        params.getAll(t).forEach(function (v) { if (v) ctx.activeFilters[t].add(v); });
      } else {
        var raw = params.get(t);
        if (raw !== null) ctx.activeFilters[t] = raw;
      }
    });
    if (params.get('oos') === '1') ctx.onlyInStock = true;
    if (params.get('sale') === '1') ctx.onlyOnSale = true;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────
  function getBatch(ctx) {
    return (window.innerWidth <= 768 && window.innerHeight > window.innerWidth) ? ctx.batchMobile : ctx.batchDesktop;
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (attrs[k] === null || attrs[k] === undefined || attrs[k] === false) return;
        if (k === 'class') node.className = attrs[k];
        else node.setAttribute(k, attrs[k]);
      });
    }
    (children || []).forEach(function (c) { node.appendChild(c); });
    return node;
  }
  function text(s) { return document.createTextNode(s); }

  function chevronSvg() {
    var svgNS = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('width', '13'); svg.setAttribute('height', '13');
    svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '2.5');
    svg.setAttribute('aria-hidden', 'true');
    var poly = document.createElementNS(svgNS, 'polyline');
    poly.setAttribute('points', '6 9 12 15 18 9');
    svg.appendChild(poly);
    return svg;
  }
  function crossSvg() {
    var svgNS = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('width', '10'); svg.setAttribute('height', '10');
    svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '2.5');
    svg.setAttribute('aria-hidden', 'true');
    var l1 = document.createElementNS(svgNS, 'line');
    l1.setAttribute('x1', '18'); l1.setAttribute('y1', '6'); l1.setAttribute('x2', '6'); l1.setAttribute('y2', '18');
    var l2 = document.createElementNS(svgNS, 'line');
    l2.setAttribute('x1', '6'); l2.setAttribute('y1', '6'); l2.setAttribute('x2', '18'); l2.setAttribute('y2', '18');
    svg.appendChild(l1); svg.appendChild(l2);
    return svg;
  }

  function titleCase(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
  function interp(tpl, vars) {
    return tpl.replace(/\{(\w+)\}/g, function (_, k) { return vars[k] === undefined ? '' : vars[k]; });
  }
  function cssEscape(s) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    return String(s).replace(/[^\w-]/g, function (c) { return '\\' + c; });
  }
})();
